import { useState, type FormEvent } from "react";
import type { AlertPreferenceUpdate, Profile } from "../../types";
import "./SettingsPage.css";

type Props = {
  profile: Profile;
  onSave: (update: AlertPreferenceUpdate) => Promise<void>;
};

export default function SettingsPage({ profile, onSave }: Props) {
  const [fullName, setFullName] = useState(profile.fullName);
  const [enabled, setEnabled] = useState(profile.emailAlertsEnabled);
  const [alert14Days, setAlert14Days] = useState(profile.alert14Days);
  const [alert7Days, setAlert7Days] = useState(profile.alert7Days);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    try {
      await onSave({ fullName, emailAlertsEnabled: enabled, alert14Days, alert7Days });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-page">
      <section className="settings-banner">
        <div>
          <p>ACCOUNT SETTINGS</p>
          <h2>Profile &amp; Email Preferences</h2>
          <span>
            Control which scheduled service reminders are delivered to your
            account.
          </span>
        </div>
      </section>

      <form className="settings-grid" onSubmit={submit}>
        <section className="settings-card">
          <header>
            <div>
              <p>PROFILE</p>
              <h3>Signed-in account</h3>
            </div>
            <span className={`role-badge ${profile.role.toLowerCase()}`}>
              {profile.role}
            </span>
          </header>
          <div className="settings-body">
            <label>
              Display name
              <input
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                required
              />
            </label>
            <label>
              Alert email address
              <input value={profile.email} readOnly />
              <small>
                Ask an administrator to correct this address if it is not your
                real delivery email.
              </small>
            </label>
            <label>
              Role
              <input value={profile.role} readOnly />
            </label>
          </div>
        </section>

        <section className="settings-card">
          <header>
            <div>
              <p>NOTIFICATIONS</p>
              <h3>Service reminder windows</h3>
            </div>
            <span className={`delivery-state ${enabled ? "on" : "off"}`}>
              {enabled ? "Enabled" : "Disabled"}
            </span>
          </header>
          <div className="settings-body toggles">
            <label>
              <span>
                <strong>Email service alerts</strong>
                <small>Master switch for scheduled reminder emails</small>
              </span>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
            </label>
            <label>
              <span>
                <strong>14-day reminder</strong>
                <small>Send once while service is 8–14 days away</small>
              </span>
              <input
                type="checkbox"
                checked={alert14Days}
                onChange={(event) => setAlert14Days(event.target.checked)}
                disabled={!enabled}
              />
            </label>
            <label>
              <span>
                <strong>7-day reminder</strong>
                <small>Send once while service is 0–7 days away</small>
              </span>
              <input
                type="checkbox"
                checked={alert7Days}
                onChange={(event) => setAlert7Days(event.target.checked)}
                disabled={!enabled}
              />
            </label>
            <aside>
              The secure worker runs daily. Browser users cannot view or edit the
              private email outbox.
            </aside>
          </div>
        </section>

        <div className="settings-actions">
          <button type="submit" disabled={saving || !fullName.trim()}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
